import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Brain, BarChart3, PieChart, Building2, Calendar,
  DollarSign, Eye, FileText, Landmark, Globe, Users, Target,
  ChevronLeft, ChevronRight, Shield, Award, TrendingUp,
  MapPin, Clock, ArrowUpDown, ArrowUp, ArrowDown, ScrollText
} from 'lucide-react';
import './ContractDetailPage.css';

// =============================================================================
// MOCK DATA — keyed by ticker
// =============================================================================

const contractDetailData = {
  'LMT': {
    ticker: 'LMT', company: 'Lockheed Martin Corporation', sector: 'Aerospace & Defense',
    headquarters: 'Bethesda, MD', employees: 122000,
    totalContracts2025: 18400000000, totalContracts2024: 16200000000,
    totalContractsAllTime: 412000000000, activeContracts: 47,
    contractorRank: 1, sectorRank: 1,
    summary: 'Lockheed Martin is the largest US defense contractor by contract value. Primary programs include the F-35 Joint Strike Fighter ($1.7 trillion lifetime), missile defense (THAAD, PAC-3), space systems (Orion, GPS III), and classified programs. The company receives contracts from 12 federal agencies, with 94% from the Department of Defense.',
    annualSpend: [
      { year: 2025, amount: 18400000000 },
      { year: 2024, amount: 16200000000 },
      { year: 2023, amount: 14800000000 },
      { year: 2022, amount: 13200000000 },
      { year: 2021, amount: 12600000000 },
      { year: 2020, amount: 11400000000 },
    ],
    agencyBreakdown: [
      { agency: 'U.S. Air Force', weight: 38, amount: 6990000000, description: 'F-35 production, C-130J, hypersonic weapons, classified programs' },
      { agency: 'U.S. Army', weight: 22, amount: 4050000000, description: 'PAC-3 missiles, THAAD, Apache upgrades, integrated air and missile defense' },
      { agency: 'U.S. Navy', weight: 18, amount: 3310000000, description: 'Aegis Combat System, VLS, littoral combat ship modules, submarine combat systems' },
      { agency: 'Missile Defense Agency', weight: 10, amount: 1840000000, description: 'Ground-based Midcourse Defense, THAAD interceptors, Next Gen Interceptor' },
      { agency: 'NASA', weight: 8, amount: 1470000000, description: 'Orion Multi-Purpose Crew Vehicle, Space Launch System components' },
      { agency: 'Space Force', weight: 4, amount: 740000000, description: 'GPS III satellites, missile warning systems, space situational awareness' },
    ],
    activeContractsList: [
      { id: 'LMT-001', program: 'F-35 Lightning II Lot 18-19', agency: 'U.S. Air Force', value: 4200000000, type: 'Definitive Contract', awarded: '2026-02-03', ends: '2029-09-30', status: 'Active', description: 'Production of 148 F-35 aircraft across A, B, and C variants with sustainment and spare parts.', location: 'Fort Worth, TX' },
      { id: 'LMT-002', program: 'Orion MPCV Production', agency: 'NASA', value: 1890000000, type: 'Cost-Plus', awarded: '2025-11-28', ends: '2030-12-31', status: 'Active', description: 'Production and integration of Orion crew vehicles for Artemis IV-VI lunar missions.', location: 'Denver, CO' },
      { id: 'LMT-003', program: 'PAC-3 MSE Missiles', agency: 'Missile Defense Agency', value: 1420000000, type: 'Definitive Contract', awarded: '2025-09-15', ends: '2028-06-30', status: 'Active', description: 'Production of PAC-3 Missile Segment Enhancement interceptors for US and allied nations.', location: 'Camden, AR' },
      { id: 'LMT-004', program: 'THAAD System Upgrade', agency: 'U.S. Army', value: 980000000, type: 'Definitive Contract', awarded: '2025-08-20', ends: '2028-12-31', status: 'Active', description: 'Terminal High Altitude Area Defense system modernization and interceptor production.', location: 'Troy, AL' },
      { id: 'LMT-005', program: 'GPS III Follow-On', agency: 'Space Force', value: 740000000, type: 'Cost-Plus', awarded: '2025-07-10', ends: '2029-03-31', status: 'Active', description: 'GPS III satellites 13-15 with enhanced M-code military signal and cyber hardening.', location: 'Waterton, CO' },
      { id: 'LMT-006', program: 'C-130J Super Hercules', agency: 'U.S. Air Force', value: 620000000, type: 'Definitive Contract', awarded: '2025-06-01', ends: '2028-09-30', status: 'Active', description: 'Multi-year procurement of 24 C-130J-30 tactical airlifters for USAF and allied forces.', location: 'Marietta, GA' },
      { id: 'LMT-007', program: 'Aegis Combat System', agency: 'U.S. Navy', value: 1150000000, type: 'Indefinite Delivery', awarded: '2025-04-12', ends: '2029-04-30', status: 'Active', description: 'Aegis Baseline 10 combat system upgrades for Arleigh Burke-class destroyers.', location: 'Moorestown, NJ' },
      { id: 'LMT-008', program: 'Classified Program', agency: 'Intelligence Community', value: 890000000, type: 'Cost-Plus', awarded: '2025-03-01', ends: '2028-02-28', status: 'Active', description: 'Classified satellite and intelligence systems development and production.', location: 'Sunnyvale, CA' },
    ],
    contractTypes: [
      { type: 'Definitive Contract', count: 24, value: 9800000000, color: '#8b5cf6' },
      { type: 'Cost-Plus', count: 12, value: 5200000000, color: '#3b82f6' },
      { type: 'Indefinite Delivery', count: 8, value: 2600000000, color: '#f59e0b' },
      { type: 'Time & Materials', count: 3, value: 800000000, color: '#10b981' },
    ],
    relatedCongressional: [
      { politician: 'Kay Granger', party: 'R', chamber: 'House', action: 'Purchased LMT shares', amount: '$50K - $100K', date: '2026-01-28', relevance: 'Former Chair, House Appropriations — oversaw defense budget' },
      { politician: 'Jack Reed', party: 'D', chamber: 'Senate', action: 'Purchased LMT shares', amount: '$15K - $50K', date: '2025-12-15', relevance: 'Chair, Senate Armed Services Committee' },
      { politician: 'Jim Inhofe', party: 'R', chamber: 'Senate', action: 'Held LMT shares', amount: '$100K - $250K', date: '2025-11-01', relevance: 'Former Ranking Member, Senate Armed Services' },
    ],
    aiInsight: "Lockheed Martin's contract pipeline reveals a company at the center of every major US defense priority. The F-35 program alone accounts for 23% of annual contract value, with Lots 18-19 representing the shift to full-rate production. The 13.6% YoY increase in contract value is driven by missile defense surge spending (+28% for PAC-3/THAAD) responding to global threat escalation. Notable: Lockheed has quietly become NASA's largest contractor through the Orion program ($1.89B), positioning for Artemis lunar missions. The congressional overlap is significant — both the House Appropriations chair and Senate Armed Services chair traded LMT shares while setting the defense budget that funds these contracts.",
  },
  'AMZN': {
    ticker: 'AMZN', company: 'Amazon Web Services Inc.', sector: 'Cloud Computing / Technology',
    headquarters: 'Seattle, WA', employees: 1540000,
    totalContracts2025: 4800000000, totalContracts2024: 3200000000,
    totalContractsAllTime: 18600000000, activeContracts: 14,
    contractorRank: 8, sectorRank: 1,
    summary: 'Amazon\'s federal contracting is driven almost entirely by AWS cloud services, with the JWCC (Joint Warfighting Cloud Capability) as its flagship defense contract. AWS also holds major contracts with the CIA, NSA, and civilian agencies for cloud migration and AI/ML platforms.',
    annualSpend: [
      { year: 2025, amount: 4800000000 },
      { year: 2024, amount: 3200000000 },
      { year: 2023, amount: 2400000000 },
      { year: 2022, amount: 1800000000 },
      { year: 2021, amount: 1200000000 },
      { year: 2020, amount: 800000000 },
    ],
    agencyBreakdown: [
      { agency: 'Defense Info Systems Agency', weight: 40, amount: 1920000000, description: 'JWCC multi-cloud, classified cloud regions, data migration' },
      { agency: 'Intelligence Community', weight: 25, amount: 1200000000, description: 'C2S (Commercial Cloud Services) for CIA, classified workloads' },
      { agency: 'U.S. Army', weight: 15, amount: 720000000, description: 'Army Enterprise Cloud, tactical edge computing, AI/ML platforms' },
      { agency: 'Dept. of Veterans Affairs', weight: 10, amount: 480000000, description: 'VA.gov modernization, electronic health records cloud hosting' },
      { agency: 'General Services Admin', weight: 10, amount: 480000000, description: 'FedRAMP cloud services across civilian agencies' },
    ],
    activeContractsList: [
      { id: 'AMZN-001', program: 'JWCC Cloud Infrastructure', agency: 'DISA', value: 1240000000, type: 'Indefinite Delivery', awarded: '2026-01-18', ends: '2028-06-30', status: 'Active', description: 'Joint Warfighting Cloud Capability — enterprise cloud services for DoD at all classification levels.', location: 'Arlington, VA' },
      { id: 'AMZN-002', program: 'C2S Intelligence Cloud', agency: 'Intelligence Community', value: 980000000, type: 'Indefinite Delivery', awarded: '2025-10-01', ends: '2028-09-30', status: 'Active', description: 'Commercial Cloud Services for classified intelligence workloads.', location: 'Herndon, VA' },
      { id: 'AMZN-003', program: 'Army Enterprise Cloud', agency: 'U.S. Army', value: 420000000, type: 'Indefinite Delivery', awarded: '2025-08-15', ends: '2027-08-31', status: 'Active', description: 'Cloud migration and managed services for Army enterprise IT systems.', location: 'Fort Belvoir, VA' },
      { id: 'AMZN-004', program: 'VA.gov Modernization', agency: 'Dept. of Veterans Affairs', value: 310000000, type: 'Blanket Purchase', awarded: '2025-06-01', ends: '2028-05-31', status: 'Active', description: 'Cloud hosting, CDN, and serverless compute for VA digital services.', location: 'Washington, DC' },
    ],
    contractTypes: [
      { type: 'Indefinite Delivery', count: 9, value: 3400000000, color: '#3b82f6' },
      { type: 'Blanket Purchase', count: 3, value: 880000000, color: '#10b981' },
      { type: 'Time & Materials', count: 2, value: 520000000, color: '#f59e0b' },
    ],
    relatedCongressional: [
      { politician: 'Mark Kelly', party: 'D', chamber: 'Senate', action: 'Purchased AMZN shares', amount: '$100K - $250K', date: '2026-01-20', relevance: 'Member of Senate Commerce Committee' },
    ],
    aiInsight: "AWS's government contract portfolio has grown 500% since 2020, making it the fastest-growing federal contractor in our dataset. The 50% YoY jump in 2025 is driven by JWCC task orders accelerating as DoD migrates workloads from legacy data centers. Key insight: AWS is the only JWCC vendor with classified cloud regions already operational (C2S for CIA since 2013), giving it a structural advantage over Microsoft, Oracle, and Google in winning classified task orders. The VA.gov contract is a sleeper — if successful, it becomes the template for cloud modernization across all civilian agencies.",
  },
};

// Fallback
const generateDefault = (ticker) => ({
  ticker, company: `${ticker} Corporation`, sector: 'Unknown',
  headquarters: 'N/A', employees: 0,
  totalContracts2025: 500000000, totalContracts2024: 420000000,
  totalContractsAllTime: 2800000000, activeContracts: 5,
  contractorRank: 40, sectorRank: 8,
  summary: `${ticker} is a publicly traded company with active federal contract activity.`,
  annualSpend: [
    { year: 2025, amount: 500000000 }, { year: 2024, amount: 420000000 },
    { year: 2023, amount: 380000000 },
  ],
  agencyBreakdown: [
    { agency: 'Various Federal Agencies', weight: 100, amount: 500000000, description: 'Federal procurement across multiple agencies' },
  ],
  activeContractsList: [],
  contractTypes: [
    { type: 'Definitive Contract', count: 3, value: 300000000, color: '#8b5cf6' },
    { type: 'Indefinite Delivery', count: 2, value: 200000000, color: '#3b82f6' },
  ],
  relatedCongressional: [],
  aiInsight: `${ticker}'s government contract activity is moderate. Limited public data available for detailed analysis.`,
});

// =============================================================================
// HELPERS
// =============================================================================
const formatCurrency = (num) => {
  if (!num && num !== 0) return '—';
  if (Math.abs(num) >= 1e12) return `$${(num / 1e12).toFixed(1)}T`;
  if (Math.abs(num) >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(0)}M`;
  if (Math.abs(num) >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function ContractDetailPage() {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const sym = (ticker || '').toUpperCase();
  const data = contractDetailData[sym] || generateDefault(sym);

  const yoyChange = ((data.totalContracts2025 - data.totalContracts2024) / data.totalContracts2024 * 100).toFixed(1);
  const maxAnnual = Math.max(...data.annualSpend.map(a => a.amount));
  const maxAgency = Math.max(...data.agencyBreakdown.map(a => a.weight));
  const maxType = Math.max(...data.contractTypes.map(t => t.value));

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'contracts', label: `Active Contracts (${data.activeContractsList.length})` },
    { id: 'congressional', label: 'Congressional Overlap' },
  ];

  return (
    <div className="contract-detail-page">
      {/* Back */}
      <div className="cd-header">
        <button className="cd-back-btn" onClick={() => navigate('/datasets/gov-contracts')}>
          <ArrowLeft size={18} /> Government Contracts
        </button>
      </div>

      {/* Company Hero */}
      <div className="cd-company-header">
        <div className="cd-company-left">
          <div className="cd-company-icon"><Shield size={24} /></div>
          <div>
            <div className="cd-company-top">
              <h1>{data.company}</h1>
              <span className="cd-ticker-badge">{data.ticker}</span>
            </div>
            <p className="cd-company-meta">{data.sector} • {data.headquarters} • {data.employees.toLocaleString()} employees</p>
          </div>
        </div>
        <button className="cd-stock-btn" onClick={() => navigate(`/stock/${data.ticker}?tab=government`)}>
          <Eye size={14} /> View Stock
        </button>
      </div>

      {/* Stats */}
      <div className="cd-stats-row">
        <div className="cd-stat">
          <div className="cd-stat-icon purple"><DollarSign size={18} /></div>
          <span className="cd-stat-label">2025 Contract Value</span>
          <span className="cd-stat-value">{formatCurrency(data.totalContracts2025)}</span>
          <span className={`cd-stat-change ${yoyChange >= 0 ? 'positive' : 'negative'}`}>{yoyChange >= 0 ? '+' : ''}{yoyChange}% YoY</span>
        </div>
        <div className="cd-stat">
          <div className="cd-stat-icon green"><TrendingUp size={18} /></div>
          <span className="cd-stat-label">All-Time Value</span>
          <span className="cd-stat-value">{formatCurrency(data.totalContractsAllTime)}</span>
          <span className="cd-stat-sub">Since tracking began</span>
        </div>
        <div className="cd-stat">
          <div className="cd-stat-icon amber"><FileText size={18} /></div>
          <span className="cd-stat-label">Active Contracts</span>
          <span className="cd-stat-value">{data.activeContracts}</span>
          <span className="cd-stat-sub">Current awards</span>
        </div>
        <div className="cd-stat">
          <div className="cd-stat-icon red"><Award size={18} /></div>
          <span className="cd-stat-label">Contractor Rank</span>
          <span className="cd-stat-value">#{data.contractorRank}</span>
          <span className="cd-stat-sub">#{data.sectorRank} in {data.sector}</span>
        </div>
      </div>

      {/* AI */}
      <div className="cd-ai-card">
        <div className="cd-ai-badge"><Brain size={14} /> AI Insight</div>
        <p>{data.aiInsight}</p>
      </div>

      {/* Tabs */}
      <div className="cd-tabs">
        {tabs.map(tab => (
          <button key={tab.id} className={`cd-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>{tab.label}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeTab === 'overview' && (
        <div className="cd-tab-content">
          <div className="cd-grid-2col">
            {/* Agency Breakdown */}
            <div className="cd-panel">
              <h3 className="cd-panel-title"><Landmark size={16} /> Who Awards the Contracts</h3>
              <p className="cd-panel-desc">{data.summary}</p>
              <div className="cd-issue-list">
                {data.agencyBreakdown.map((item, idx) => (
                  <div key={idx} className="cd-issue-item">
                    <div className="cd-issue-header">
                      <span className="cd-issue-name">{item.agency}</span>
                      <span className="cd-issue-pct">{item.weight}% • {formatCurrency(item.amount)}</span>
                    </div>
                    <div className="cd-issue-bar-bg"><div className="cd-issue-bar" style={{ width: `${(item.weight / maxAgency) * 100}%` }}></div></div>
                    <p className="cd-issue-desc">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right column */}
            <div className="cd-panel">
              <h3 className="cd-panel-title"><BarChart3 size={16} /> Annual Contract Value</h3>
              <div className="cd-bar-chart">
                {data.annualSpend.map((a, idx) => (
                  <div key={idx} className="cd-bar-row">
                    <span className="cd-bar-label">{a.year}</span>
                    <div className="cd-bar-track"><div className="cd-bar-fill" style={{ width: `${(a.amount / maxAnnual) * 100}%` }}></div></div>
                    <span className="cd-bar-value">{formatCurrency(a.amount)}</span>
                  </div>
                ))}
              </div>

              <h3 className="cd-panel-title" style={{ marginTop: 28 }}><PieChart size={16} /> Contract Types</h3>
              <div className="cd-type-list">
                {data.contractTypes.map((t, idx) => (
                  <div key={idx} className="cd-type-row">
                    <div className="cd-type-info">
                      <div className="cd-type-dot" style={{ background: t.color }}></div>
                      <span className="cd-type-name">{t.type}</span>
                    </div>
                    <div className="cd-type-bar-bg"><div className="cd-type-bar" style={{ width: `${(t.value / maxType) * 100}%`, background: t.color }}></div></div>
                    <div className="cd-type-stats">
                      <span className="cd-type-val">{formatCurrency(t.value)}</span>
                      <span className="cd-type-count">{t.count} awards</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ACTIVE CONTRACTS */}
      {activeTab === 'contracts' && (
        <div className="cd-tab-content">
          {data.activeContractsList.length === 0 ? (
            <div className="cd-empty"><ScrollText size={32} /><p>No active contracts tracked for {data.ticker}</p></div>
          ) : (
            <div className="cd-contracts-list">
              {data.activeContractsList.map((c, idx) => (
                <div key={idx} className="cd-contract-card">
                  <div className="cd-contract-top">
                    <div>
                      <h4 className="cd-contract-program">{c.program}</h4>
                      <span className="cd-contract-agency">{c.agency}</span>
                    </div>
                    <div className="cd-contract-value-col">
                      <span className="cd-contract-value">{formatCurrency(c.value)}</span>
                      <span className={`cd-contract-status ${c.status.toLowerCase()}`}>{c.status}</span>
                    </div>
                  </div>
                  <p className="cd-contract-desc">{c.description}</p>
                  <div className="cd-contract-meta">
                    <span><Calendar size={12} /> Awarded: {new Date(c.awarded).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    <span><Clock size={12} /> Ends: {new Date(c.ends).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    <span><MapPin size={12} /> {c.location}</span>
                    <span className="cd-contract-type-badge">{c.type}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CONGRESSIONAL OVERLAP */}
      {activeTab === 'congressional' && (
        <div className="cd-tab-content">
          {data.relatedCongressional.length === 0 ? (
            <div className="cd-empty"><Landmark size={32} /><p>No congressional trading overlap detected for {data.ticker}</p></div>
          ) : (
            <>
              <p className="cd-overlap-desc">Members of Congress who traded {data.ticker} while serving on committees that oversee contracts awarded to the company.</p>
              <div className="cd-congress-list">
                {data.relatedCongressional.map((c, idx) => (
                  <div key={idx} className="cd-congress-row" onClick={() => navigate(`/politician/${c.politician.toLowerCase().replace(/\s/g, '-')}`)}>
                    <div className="cd-congress-info">
                      <span className="cd-congress-name">{c.politician}</span>
                      <span className={`cd-congress-party ${c.party === 'D' ? 'dem' : 'rep'}`}>{c.party === 'D' ? 'Democrat' : 'Republican'} • {c.chamber}</span>
                    </div>
                    <div className="cd-congress-action">
                      <span className={c.action.includes('Purchased') || c.action.includes('Held') ? 'positive' : 'negative'}>{c.action}</span>
                      <span className="cd-congress-amount">{c.amount}</span>
                    </div>
                    <div className="cd-congress-date">{new Date(c.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                    <div className="cd-congress-relevance">{c.relevance}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}